import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Document, Page, pdfjs } from "react-pdf";
import { useParams } from "react-router-dom";
import "../Units/EditUnit.css";
import { Edit, Delete } from '@mui/icons-material';
import { Snackbar, Alert } from '@mui/material';
import { Modal } from "react-bootstrap";
import { Icon } from "@iconify/react/dist/iconify.js";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
 
// Setup PDF worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
 
const toolbarOptions = [
  [{ font: [] }],
  [{ size: [] }],
  ["bold", "italic", "underline", "strike"],
  [{ script: "sub" }, { script: "super" }],
  [{ align: [] }],
  ["blockquote"],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ color: [] }, { background: [] }],
  ["link"],
  ["clean"],
];
 
const TextEditor = ({ value, onChange }) => {
  return (
    <ReactQuill
      theme="snow"
      value={value}
      onChange={onChange}
      modules={{
        toolbar: toolbarOptions,
        table: true,
      }}
    />
  );
};
 
function UnitView() {
  const { id } = useParams();
  const [contentData, setContentData] = useState([]);
  const [fullContentData, setFullContentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedContent, setEditedContent] = useState("");
  const [contentSections, setContentSections] = useState([]);
  const [pdfLoading, setpdfLoading] = useState(true);
  const [videoLoading, setVideoLoading] = useState(true);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
 
  const containerRef = useRef(null);
 
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
 
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
 
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };
 
  const handleDeleteClick = (index) => {
    setItemToDelete(index);
    setShowDeleteModal(true);
  };
 
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };
 
  const handleParagraphChange = (index, value) => {
    const updatedSections = [...contentSections];
    updatedSections[index].data = value;
    setContentSections(updatedSections);
  };
 
  const handleConfirmDelete = async () => {
    try {
      await handleDelete(itemToDelete);
      handleCloseDeleteModal();
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };
 
  const renderHTMLContent = (content) => {
    if (!content) return null;
    const decodedContent = content
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&');
    return (
      <div
        dangerouslySetInnerHTML={{ __html: decodedContent }}
        className="content-text"
      />
    );
  };
 
  useEffect(() => {
    const fetchUnit = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_INSTITUTION_BASEURL}/unit/ui/${id}`
        );
        if (response.data && response.data.unitsPlans) {
          setFullContentData(response.data);
          setContentData(response.data.unitsPlans);
        } else {
          setError("No units plans found");
        }
      } catch (err) {
        setError(err.message || "Failed to fetch unit data");
      } finally {
        setLoading(false);
      }
    };
    fetchUnit();
  }, [id]);
 
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };
 
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullScreen(false);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);
 
  const toggleFullScreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
        setIsFullScreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullScreen(false);
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err);
    }
  };
 
  const handleContentTypeChange = (event, index) =>  {
    const { value } = event.target;

    setContentData((prevContentData) => {
      // Ensure prevContentData is an object
      if (typeof prevContentData !== "object" || prevContentData === null) {
        console.error("prevContentData is not an object:", prevContentData);
        return contentData; // or handle as needed
      }

      // Make sure units_content is an array
      const unitsContent = prevContentData.units_content || [];
      if (!Array.isArray(unitsContent)) {
        console.error("units_content is not an array:", unitsContent);
        return contentData; // or handle as needed
      }
      // Determine the new section's data based on the selected value
      const newSection =
        value === "pdf" || value === "gallery"
          ? { type: value, data: [""], edit: 1 } // Use an array for pdf and gallery
          : { type: value, data: "", edit: 1 }; // Use an empty string for other types
      const updatedSections = [...unitsContent];
      updatedSections.splice(index + 1, 0, newSection);
      // console.log(updatedSections);

      // Return a new object with updated units_content
      return {
        ...prevContentData,
        units_content: updatedSections,
      };
    });
    setEditingIndex(index+1);
    // setEditedContent(Array.isArray(content) ? content[0] || "" : content || "");
    // setEditing(true);
  };
 
  const handleRemoveSection = (index) => {
    const newContentSections = [...contentSections];
    newContentSections.splice(index, 1);
    setContentSections(newContentSections);
  };
 
  const handleGalleryChange = (index, event) => {
    const updatedSections = [...contentSections];
    updatedSections[index].galleryFiles = event.target.files;
    updatedSections[index].count = event.target.files?.length;
    setContentSections(updatedSections);
  };
 
  const handleAddSection = () => {
    setContentSections([
      ...contentSections,
      {
        contentType: "Heading1",
        data: "",
        id: Date.now(),
      },
    ]);
  };
 
  const handleClickOpen = (imageSrc) => {
    setSelectedImage(imageSrc);
    setOpen(true);
  };
 
  const handleClose = () => {
    setOpen(false);
    setSelectedImage("");
  };
 
  const handleEdit = (index, content) => {
    setEditingIndex(index);
    setEditedContent(Array.isArray(content) ? content[0] || "" : content || "");
  };
 
  const handleSave = async (index) => {
    try {
      const updatedContent = [...contentData.units_content];
      updatedContent[index].data = [editedContent];
      const token = localStorage.getItem("token");
      if (!token) {
        setSnackbarMessage("Access required");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        return;
      }
      const response = await axios.put(
        `${process.env.REACT_APP_INSTITUTION_BASEURL}/editunit/${id}`,
        {
          content: updatedContent,
          unit_id: fullContentData.units._id,
          id: fullContentData.unitsPlans._id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.status === 200) {
        setContentData({ ...contentData, units_content: updatedContent });
        setEditingIndex(null);
        setEditedContent("");
        setSnackbarMessage("Content updated successfully!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      } else {
        setSnackbarMessage("Failed to update content");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      setSnackbarMessage("Error saving changes");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };
 
  const handleSaveFile = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        // console.log("Access required");
        return;
      }
      const response = await axios.put(
        `${process.env.REACT_APP_INSTITUTION_BASEURL}/editunit/${id}`,
        {
          content: contentData.units_content,
          unit_id: fullContentData.units._id,
          id: fullContentData.unitsPlans._id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.status === 200) {
        setEditingIndex(null);
        setEditedContent("");
      } else {
        setError("Failed to update content");
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      setError("Error saving changes");
    }
  };
 
  const handleDelete = async (index) => {
    try {
      const updatedContent = [...contentData.units_content];
      updatedContent.splice(index, 1);
      const token = localStorage.getItem("token");
      if (!token) {
        setSnackbarMessage("Access required");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        return;
      }
      const response = await axios.put(
        `${process.env.REACT_APP_INSTITUTION_BASEURL}/editunit/${id}`,
        {
          content: updatedContent,
          unit_id: fullContentData.units._id,
          id: fullContentData.unitsPlans._id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.status === 200) {
        setContentData({ ...contentData, units_content: updatedContent });
        setSnackbarMessage("Content deleted successfully");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      } else {
        setSnackbarMessage("Failed to delete. Please try again.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("Error deleting Content:", error);
      setSnackbarMessage("Error deleting Content");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };
 
  const handlePdfUploadChange = async (index, event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }
    const formData = new FormData();
    formData.append("pdfFiles", file);
    try {
      setpdfLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_INSTITUTION_BASEURL}/uploadfile/savefile`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const updatedContentData = { ...contentData };
      if (!updatedContentData.units_content[index]) {
        updatedContentData.units_content[index] = { type: "pdf", data: [] };
      }
      updatedContentData.units_content[index].data = [response.data.fileUrl];
      setContentData(updatedContentData);
    } catch (err) {
      setError(err.message || "Failed to upload file");
    } finally {
      setpdfLoading(false);
    }
  };
 
  const handleVideoUploadChange = async (index, event) => {
    const file = event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("videoFile", file);
    try {
      setVideoLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_INSTITUTION_BASEURL}/uploadfile/savefile`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const updatedContentData = { ...contentData };
      if (!updatedContentData.units_content[index]) {
        updatedContentData.units_content[index] = {
          type: "videoUpload",
          data: [],
        };
      }
      updatedContentData.units_content[index].data = [response.data.fileUrl];
      setContentData(updatedContentData);
    } catch (err) {
      setError(err.message || "Failed to upload file");
    } finally {
      setVideoLoading(false);
    }
  };
 
  const handleSingleImageChange = async (index, event) => {
    const file = event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("imageFile", file);
    try {
      setGalleryLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_INSTITUTION_BASEURL}/uploadfile/savefile`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const updatedContentData = { ...contentData };
      updatedContentData.units_content[index].data = response.data.fileUrl;
      setContentData(updatedContentData);
    } catch (err) {
      console.error(err.message || "Failed to upload file");
    } finally {
      setGalleryLoading(false);
    }
  };
 
  return (
    <>
      <div
        ref={containerRef}
        className={`container ${isFullScreen ? "fullscreen" : ""}`}>
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          contentData.units_content &&
          contentData.units_content?.length > 0 &&
          contentData.units_content.map((item, index) => (
            <div key={index} className="content-section" style={{ position: 'relative' }}>
              <div style={{
                position: 'absolute',
                top: '18px',
                right: '21px',
                zIndex: 1,
                display: 'flex',
                gap: '10px'
              }}>
                <Edit
                  onClick={() => handleEdit(index, item.data)}
                  sx={{
                    cursor: 'pointer',
                    color: '#007bff',
                    '&:hover': {
                      color: '#0056b3'
                    }
                  }}
                />
                <Delete
                  onClick={() => handleDeleteClick(index)}
                  sx={{
                    cursor: 'pointer',
                    color: '#dc3545',
                    '&:hover': {
                      color: '#c82333'
                    }
                  }}
                />
              </div>
 
              {editingIndex === index ? (
                <div className="edit-container" style={{ marginTop: '20px' }}>
                  {item.type === "Paragraph"? (
                    <>
                      <ReactQuill
                        value={editedContent}
                        onChange={setEditedContent}
                        style={{
                          width: '100%',
                          minHeight: '100px',
                          marginBottom: '10px'
                        }}
                        modules={{ toolbar: toolbarOptions }}
                      />
                      <button
                        onClick={() => handleSave(index)}
                        style={{
                          padding: '5px 15px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Save
                      </button>
                    </>
                  ) : item.type === "unitHeading" ||
                  item.type === "Heading1" || item.type === "Heading2" ? (
                  <>
                    <textarea
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                      style={{
                        width: '100%',
                        minHeight: '100px',
                        padding: '10px',
                        marginBottom: '10px'
                      }}
                    />
                    <button
                      onClick={() => handleSave(index)}
                      style={{
                        padding: '5px 15px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Save
                    </button>
                  </>
                ) : (
                    <>
                      <input
                        type="file"
                        onChange={(e) => {
                          if (item.type === "PDF") {
                            handlePdfUploadChange(index, e);
                          } else if (item.type === "videoUpload") {
                            handleVideoUploadChange(index, e);
                          } else if (item.type === "Singleimage") {
                            handleSingleImageChange(index, e);
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '10px',
                          marginBottom: '10px'
                        }}
                      />
                      <button
                        onClick={() => handleSaveFile()}
                        style={{
                          padding: '5px 15px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Save
                      </button>
                    </>
                  )}
                </div>
              ) : (
                (() => {
                  switch (item.type) {
                    case "unitHeading":
                      return (
                        <section key={index} className="unit-heading">
                          <h1>{item.data}</h1>
                        </section>
                      );
                    case "Heading1":
                      return (
                        <section key={index} className="heading1">
                          <h1>{item.data}</h1>
                        </section>
                      );
                    case "Heading2":
                      return (
                        <section key={index} className="heading2">
                          <h2>{item.data}</h2>
                        </section>
                      );
                    case "Paragraph":
                      return (
                        <div
                          key={index}
                          style={{
                            marginTop: "20px",
                            padding: "20px",
                            color: "#fff",
                            backgroundColor: "#273142",
                          }}>
                          <style>
                            {`
                              .ql-size-small {
                                font-size: 12px;
                              }
                              .ql-size-large {
                                font-size: 20px;
                              }
                              .ql-size-huge {
                                font-size: 30px;
                              }
                              .ql-align-justify {
                                text-align: justify;
                              }
                              .ql-align-center {
                                text-align: center;
                              }
                              .ql-align-right {
                                text-align: right;
                              }
                              .ql-font-monospace {
                                font-family: monospace;
                              }
                              em {
                                font-style: italic;
                              }
                                  u {
                                text-decoration: underline;
                              }
                              strong {
                                font-weight: bold;
                              }
                                .ql-font-serif {
                                font-family: 'Times New Roman', serif; /* Serif font */
                              }
                              .ql-font-monospace {
                                 font-family: 'Courier New', monospace; /* Monospace font */
                                 white-space: pre-wrap; /* This ensures that whitespace and line breaks are preserved */
                                   }
                                  ul {
                                  list-style-type: disc; /* Default bullet points */
                                 margin-left: 20px; /* Indent the list slightly */
                                    }
                              li {
                                margin-bottom: 5px; /* Add some space between list items */
                              }
                            `}
                          </style>
                          <div
                            dangerouslySetInnerHTML={{ __html: item.data }}
                          />
                        </div>
                      );
                    case "PDF":
                      return (
                        <section
                          key={index}
                          style={{ width: "100%", marginLeft: "0px", paddingTop: "14px" }}>
                          {/* Loop through the PDF URLs */}
                          {item.data.map((url, pdfIndex) => (
 
                            <section
                              key={pdfIndex}
                              style={{
                                marginBottom: "16px",
                                borderRadius: "8px",
                                backgroundColor: "#fff",
                                width: "100%",
                                overflow: "hidden",
                              }}>
 
                              {/* Error Message */}
                              {error && (
                                <p style={{ color: "red", marginBottom: "16px" }}>
                                  Error: {error.message}
                                </p>
                              )}
 
                              {/* PDF Rendering */}
                              <section style={{ maxHeight: "auto", width: "100%" }}>
                                {/* Use Document component for rendering the PDF */}
                                <Document
                                  file={url}
                                  onLoadSuccess={onDocumentLoadSuccess}
                                  onLoadError={setError}
                                  loading={
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        padding: "16px",
                                      }}>
                                      Loading...
                                    </div>
                                  }
                                  style={{
                                    width: "100% !important",
                                    height: "auto",
                                  }}>
                                  {/* Loop through and render each page */}
                                  {Array.from(
                                    new Array(numPages),
                                    (el, pageIndex) => (
                                      <section
                                        key={`page_${pageIndex + 1}`}
                                        style={{ marginBottom: "16px" }}>
                                        <Page
                                          pageNumber={pageIndex + 1}
                                          renderTextLayer={false}
                                          renderAnnotationLayer={false}
                                          scale={0.8} // Control zoom level, adjust the scale as needed
                                          width={
                                            document.querySelector("body").offsetWidth
                                          } // Adjust to 100% width of the container
                                        />
                                      </section>
                                    )
                                  )}
                                </Document>
                              </section>
                            </section>
                          ))}
                        </section>
                      );
                    case "gallery":
                      return (
                        <section key={index} className="gallery">
                          {item.data.map((url, i) => (
                            <div key={i} className="gallery-item">
                              <img
                                src={url}
                                alt="Gallery"
                                onClick={() => handleClickOpen(url)}
                              />
                            </div>
                          ))}
                          {open && (
                            <div className="image-popup">
                              <span className="close" onClick={handleClose}>
                                ×
                              </span>
                              <img src={selectedImage} alt="Selected" />
                            </div>
                          )}
                        </section>
                      );
                    case "videoUpload":
                      return (
                        <section key={index} className="video-container">
                          <video
                            controls
                            preload="auto"
                            onError={(e) => {
                              console.error("Error loading video:", e);
                            }}>
                            <source src={item.data[0]} type="video/mp4" />
                            <source src={item.data[0]} type="video/webm" />
                            Your browser does not support the video tag.
                          </video>
                        </section>
                      );
                    case "Singleimage":
                      return (
                        <section
                          key={index}
                          style={{ width: "100%", marginTop: "20px" }}>
                          <img
                            src={item.data}
                            alt="Gallery"
                            onClick={() => handleClickOpen(item.data)}
                            style={{
                              width: "100%",
                              height: "auto",
                            }}
                          />
 
                          {/* Modal - overlay and content */}
                          {open && (
                            <div
                              style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                backgroundColor: "rgba(0, 0, 0, 0.7)",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                zIndex: 1000,
                              }}
                              onClick={handleClose} // Close modal on click outside
                            >
                              <div
                                style={{
                                  position: "relative",
                                  maxWidth: "90%",
                                  maxHeight: "90%",
                                  backgroundColor: "#273142",
                                  padding: "20px",
                                  boxSizing: "border-box",
                                }}
                                onClick={(e) => e.stopPropagation()} // Prevent modal closing when clicking inside content
                              >
                                <img
                                  src={selectedImage}
                                  alt="Selected"
                                  style={{
                                    width: "100%",
                                    height: "auto",
                                    maxHeight: "80vh", // Keep image inside the viewport
                                    objectFit: "contain",
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </section>
                      );
                    case "youtubeUrl":
                      return (
                        <section key={index} className="youtube-url">
                          <iframe
                            width="100%"
                            height="920px"
                            src={`https://www.youtube.com/embed/${item.youtubeUrl.split("v=")[1]
                              }`}
                            title="YouTube Video"
                            frameBorder="0"
                            allowFullScreen></iframe>
                        </section>
                      );
                    case "vimeoUrl":
                      return (
                        <section key={index} className="vimeo-url">
                          <iframe
                            src={`https://player.vimeo.com/video/${item.vimeoUrl
                              .split("/")
                              .pop()}`}
                            width="100%"
                            height="920px"
                            title="Vimeo Video"
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen></iframe>
                        </section>
                      );
                      
                    default:
                      return null;
                  }
                })()
              )}
              <select
                      // value={section.contentType}
                      onChange={(e) => handleContentTypeChange(e, index)}
                      className="form-control radius-8 form-select"
                    >
                      <option value="Heading1">Heading 1</option>
                      <option value="Heading2">Heading 2</option>
                      <option value="Paragraph">Paragraph</option>
                      <option value="videoUpload">Video Upload</option>
                      <option value="PDF">PDF Upload</option>
                      <option value="Singleimage">Single Image</option>
                    </select>
            </div>
          ))
        )}
        
      </div>
 
      {/* Fullscreen Button */}
      <div className="fullscreen-button">
        <button onClick={toggleFullScreen}>
          {isFullScreen ? "Exit Fullscreen" : "Enter Fullscreen"}
        </button>
      </div>
 
      {/* Add Snackbar component at the end of the return statement */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
 
      {/* Add the Modal component before the closing tag */}
      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        centered
        backdrop="static"
        size="sm"
      >
        <div className="modal-content radius-16 bg-base">
          <div className="modal-body p-24 text-center">
            <span className="mb-16 fs-1 line-height-1 text-danger d-block">
              <Icon icon="fluent:delete-24-regular" className="menu-icon" />
            </span>
            <h6 className="text-lg fw-semibold text-primary-light mb-0">
              Are you sure you want to delete this content?
            </h6>
            <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
              <button
                type="button"
                onClick={handleCloseDeleteModal}
                className="w-50 border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-11 radius-8"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="w-50 btn btn-primary border border-primary-600 text-md px-24 py-12 radius-8"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
 
export default UnitView;